/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaNavigatorRow.tsx
 * FILE_ID: QA-UI-NAVIGATOR-ROW-001
 * PURPOSE: One QA row in the QAs or Grouped QA's box.
 */
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import { Copy, ExternalLink, Pencil, Trash2 } from 'lucide-react'
import { confirmDialog, useLongPress } from '@quotech/ui'
import { qaHubRecordUrl } from '../../qa/qaCrossDoorLinks'
import { qaInspectionStatusLabel } from '../../qa/qaInspectionStatusLabel'
import { InlineRenameInput } from '../../qa/InlineRenameInput'
import type { QaInspectionSummary } from '../../qa/qaApi'

type RowMenuEvent = MouseEvent | { clientX: number; clientY: number }

export function QaNavigatorRow({
  inspection,
  active,
  selected,
  iconButton,
  renaming = false,
  renameRowId = null,
  onRenameRowStarted,
  onToggle,
  onSelect,
  onDuplicate,
  onRenameSubmit,
  onDelete,
  onOpenMenu,
}: {
  inspection: QaInspectionSummary
  active: boolean
  selected: boolean
  iconButton: string
  renaming?: boolean
  renameRowId?: string | null
  onRenameRowStarted?: () => void
  onToggle: () => void
  onSelect: () => void
  onDuplicate: () => void
  onRenameSubmit: (inspection: QaInspectionSummary, name: string) => void
  onDelete: () => void
  onOpenMenu: (event: RowMenuEvent) => void
}) {
  const name = inspection.inspection_name
  const [editing, setEditing] = useState(false)
  // Reused from: packages/job-hub/src/jobs/qa/QaChecklistRow.tsx (useLongPress, onContextMenu)
  // Reused from: packages/ui/src/mobile/useLongPress.ts (useLongPress)
  const pressAt = useRef({ x: 0, y: 0 })
  const openedAt = useRef(0)
  const longPress = useLongPress<void>(
    () => {
      openedAt.current = Date.now()
      onOpenMenu({ clientX: pressAt.current.x, clientY: pressAt.current.y })
    },
    { enableContextMenu: false, delayMs: 500 },
  )
  const press = longPress.bind(undefined)
  useEffect(() => {
    if (renameRowId !== inspection.id) return
    setEditing(true)
    onRenameRowStarted?.()
  }, [renameRowId, inspection.id, onRenameRowStarted])

  return (
    <div
      className={`group/qa-row flex min-h-11 items-center gap-2 px-2${active ? ' bg-primary/15' : ''}`}
      title="Right-click or long-press for row actions"
      onContextMenu={(event) => {
        event.preventDefault()
        event.stopPropagation()
        onOpenMenu(event)
      }}
      onMouseDown={(event) => {
        if (Date.now() - openedAt.current < 700) {
          event.preventDefault()
          event.stopPropagation()
        }
      }}
      onClickCapture={press.onClickCapture}
      onTouchStart={(event) => {
        const touch = event.touches[0]
        if (touch) pressAt.current = { x: touch.clientX, y: touch.clientY }
        press.onTouchStart(event)
      }}
      onTouchMove={press.onTouchMove}
      onTouchEnd={press.onTouchEnd}
      onTouchCancel={press.onTouchCancel}
    >
      <input
        type="checkbox"
        checked={selected}
        aria-label={`Select ${name}`}
        onChange={onToggle}
        className="h-4 w-4 shrink-0"
      />
      {editing ? (
        // Reused from: packages/job-hub/src/jobs/qa/InlineRenameInput.tsx (InlineRenameInput)
        <InlineRenameInput
          initialValue={name}
          busy={renaming}
          ariaLabel={`Rename ${name}`}
          className="min-w-0 flex-1"
          onSubmit={(next) => {
            onRenameSubmit(inspection, next)
            setEditing(false)
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <div
          role="button"
          tabIndex={0}
          className="min-w-0 flex-1 truncate text-sm"
          onClick={onSelect}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              onSelect()
            }
          }}
        >
          {name}
          <span className="ml-2 text-[11px] capitalize text-muted-foreground">
            {qaInspectionStatusLabel(inspection.status)}
          </span>
        </div>
      )}
      <a
        href={qaHubRecordUrl(inspection.id)}
        target="_blank"
        rel="noopener noreferrer"
        className={iconButton}
        title="Open in QA hub"
        aria-label={`Open ${name} in the QA hub`}
      >
        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      </a>
      <button
        type="button"
        className={iconButton}
        title="Rename this QA list"
        aria-label={`Rename ${name}`}
        onClick={(event) => {
          event.stopPropagation()
          setEditing(true)
        }}
      >
        <Pencil className="h-3.5 w-3.5" aria-hidden />
      </button>
      <button type="button" className={iconButton} title="Duplicate this QA list" aria-label={`Duplicate ${name}`} onClick={() => void onDuplicate()}>
        <Copy className="h-3.5 w-3.5" aria-hidden />
      </button>
      <button
        type="button"
        className={iconButton}
        title="Delete this QA list"
        aria-label={`Delete ${name}`}
        onClick={() => {
          void confirmDialog({
            title: 'Delete 1 QA?',
            description: 'This deletes the selected QA lists.',
            confirmLabel: 'Delete',
            cancelLabel: 'Cancel',
            variant: 'danger',
          }).then((ok) => {
            if (ok) onDelete()
          })
        }}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </button>
    </div>
  )
}
